import { getStorageJson, setStorageJson } from '@/utils/storage'
import { normalizeTerminalSkin } from '@/utils/appearance'
import { normalizeLanguage } from '@/i18n/locale'

// 默认模块配置。列表页模块带 pageSize，后续模块级设置继续挂在这里。
const defaultModuleSettings = {
  home: { visible: true },
  model: { visible: true, pageSize: 8 },
  register: { visible: true, pageSize: 8 },
  product: { visible: true },
  spu: { pageSize: 30 },
  sku: { pageSize: 30 },
  route: { visible: true, pageSize: 8 },
  site: { visible: true, pageSize: 8 },
  mapstyle: { visible: true, pageSize: 8 },
  'mini-program': { visible: true },
  maintenance: { visible: true },
  'middleware-maintenance': { visible: true },
  'map-proxy-maintenance': { visible: true },
  shop: { visible: true },
  visitor: { visible: true, pageSize: 20 },
  admin: { visible: true, pageSize: 8 },
  role: { visible: true, pageSize: 8 },
  message: { visible: true, pageSize: 8 },
  category: { visible: true },
  broadcast: { visible: true },
  order: { visible: true, pageSize: 50 },
  nofly: { visible: true }
}

const savedTerminalPreferences = getStorageJson('terminalPreferences', {})
const savedUserInfo = getStorageJson('userInfo', {})
const savedModules = savedTerminalPreferences.modules || {}
const savedOrderSearch = (savedModules.order && savedModules.order.search) || {}
const modulePreferences = Object.keys(defaultModuleSettings).reduce((result, key) => {
  result[key] = {
    ...defaultModuleSettings[key],
    ...savedModules[key]
  }

  return result
}, {})

const defaultStatusBarSettings = {
  time: false,
  weather: false,
  back: false,
  refresh: true,
  fullscreen: true,
  logs: true,
  printer: true,
  voice: false,
  mqtt: true,
  shop: false,
  deviceBattery: true
}
const defaultOverviewMapControls = {
  terminalLocation: false,
  zoom: true,
  rotation: false,
  styleSwitcher: false,
  weather: false,
  follow: true,
  defaultRoutes: false,
  locations: false,
  noFlyZones: false
}
const defaultOverviewMapLayers = {
  weather: false,
  defaultRoutes: true,
  locations: true,
  restrictionZones: false
}
const defaultOverviewMapInteractions = {
  pitch: false,
  rotation: false
}
// 禁飞区、限飞区编辑页共用的地图设置，与地面站概况设置相互独立。
const defaultRestrictionMapControls = {
  zoom: true,
  rotation: false,
  styleSwitcher: false
}
const defaultRestrictionMapInteractions = {
  pitch: false,
  rotation: false
}
const defaultRouteDesignerMapControls = {
  zoom: true,
  rotation: false,
  styleSwitcher: true
}
const defaultRouteDesignerMapInteractions = {
  pitch: false,
  rotation: false
}
const defaultFlightDataMapControls = {
  zoom: true,
  rotation: false,
  styleSwitcher: false
}
const defaultFlightDataMapInteractions = {
  pitch: false,
  rotation: false
}
const mergeSupportedSettings = (defaults, saved = {}) => Object.keys(defaults).reduce((result, key) => {
  result[key] = Object.prototype.hasOwnProperty.call(saved, key) ? saved[key] : defaults[key]
  return result
}, {})
const defaultOverviewCollapsedPanels = {
  mission: false,
  flight: false,
  peripheral: false
}
const supportedOverviewPlanePopupFields = [
  'name',
  'missionRoute',
  'online',
  'armed',
  'mode',
  'fixType',
  'satCount',
  'voltage',
  'current',
  'altitude',
  'longitude',
  'latitude',
  'groundSpeed'
]
const defaultOverviewPlanePopupFields = ['name', 'missionRoute']
const overviewPlanePopupDefaultsVersion = 3
const defaultOverviewTrackPointLimit = 100
const overviewTrackPointDefaultsVersion = 2
const savedOverviewPreferences = {
  ...((savedModules.home && savedModules.home.overview) || {})
}
const savedOverviewPlanePopupFields = savedOverviewPreferences.planePopupFields
// 旧版未修改过的默认值等于全部字段；只迁移这一种情况，保留用户自行选择的组合。
const hasLegacyOverviewPlanePopupDefaults = (
  savedOverviewPreferences.planePopupDefaultsVersion !== overviewPlanePopupDefaultsVersion &&
  Array.isArray(savedOverviewPlanePopupFields) &&
  ((savedOverviewPreferences.planePopupDefaultsVersion === 2 && savedOverviewPlanePopupFields.length === 1 && savedOverviewPlanePopupFields[0] === 'name') ||
    (savedOverviewPlanePopupFields.length === supportedOverviewPlanePopupFields.length - 1 && supportedOverviewPlanePopupFields.filter(key => key !== 'missionRoute').every(key => savedOverviewPlanePopupFields.includes(key))))
)
// 将旧版未区分版本的 1000 点默认值迁移为当前 100 点；其他自定义值保持不变。
const hasLegacyOverviewTrackPointDefault = (
  savedOverviewPreferences.trackPointDefaultsVersion !== overviewTrackPointDefaultsVersion &&
  Number(savedOverviewPreferences.trackPointLimit) === 1000
)
const overviewPreferences = {
  videoPipMinimized: true,
  telemetryCollapsed: true,
  fleetShopId: '',
  ...savedOverviewPreferences,
  trackPointDefaultsVersion: overviewTrackPointDefaultsVersion,
  trackPointLimit: hasLegacyOverviewTrackPointDefault
    ? defaultOverviewTrackPointLimit
    : (savedOverviewPreferences.trackPointLimit ?? defaultOverviewTrackPointLimit),
  planePopupDefaultsVersion: overviewPlanePopupDefaultsVersion,
  planePopupFields: Array.isArray(savedOverviewPlanePopupFields) && !hasLegacyOverviewPlanePopupDefaults
    ? savedOverviewPlanePopupFields
    : defaultOverviewPlanePopupFields,
  mapControls: mergeSupportedSettings(defaultOverviewMapControls, savedOverviewPreferences.mapControls),
  mapLayers: mergeSupportedSettings(defaultOverviewMapLayers, savedOverviewPreferences.mapLayers),
  mapInteractions: {
    ...defaultOverviewMapInteractions,
    ...(savedOverviewPreferences.mapInteractions || {})
  },
  collapsedPanels: {
    ...defaultOverviewCollapsedPanels,
    ...(savedOverviewPreferences.collapsedPanels || {})
  }
}
const savedRestrictionMapPreferences = (savedModules.nofly && savedModules.nofly.restrictionMap) || {}
const restrictionMapPreferences = {
  mapControls: mergeSupportedSettings(defaultRestrictionMapControls, savedRestrictionMapPreferences.mapControls),
  mapInteractions: {
    ...defaultRestrictionMapInteractions,
    ...(savedRestrictionMapPreferences.mapInteractions || {})
  }
}
const savedRouteDesignerPreferences = (savedModules.route && savedModules.route.designer) || {}
const routeDesignerPreferences = {
  mapControls: mergeSupportedSettings(defaultRouteDesignerMapControls, savedRouteDesignerPreferences.mapControls),
  mapInteractions: {
    ...defaultRouteDesignerMapInteractions,
    ...(savedRouteDesignerPreferences.mapInteractions || {})
  }
}
const savedFlightDataPreferences = (savedModules.register && savedModules.register.flightDataMap) || {}
const flightDataMapPreferences = {
  mapControls: mergeSupportedSettings(defaultFlightDataMapControls, savedFlightDataPreferences.mapControls),
  mapInteractions: {
    ...defaultFlightDataMapInteractions,
    ...(savedFlightDataPreferences.mapInteractions || {})
  }
}
let hasLegacyGuidedFov = false
const planePreferences = Object.keys(savedTerminalPreferences.plane || {}).reduce((result, key) => {
  const preference = savedTerminalPreferences.plane[key]
  if (!preference || typeof preference !== 'object' || Array.isArray(preference)) return result

  const { guidedFov, ...currentPreference } = preference
  if (guidedFov !== undefined) hasLegacyGuidedFov = true
  if (Object.keys(currentPreference).length > 0) result[key] = currentPreference
  return result
}, {})

function createDefaultModulePreferences () {
  const modules = Object.keys(defaultModuleSettings).reduce((result, key) => {
    result[key] = { ...defaultModuleSettings[key] }
    return result
  }, {})

  modules.home = {
    ...modules.home,
    overview: {
      videoPipMinimized: true,
      telemetryCollapsed: true,
      fleetShopId: '',
      trackPointDefaultsVersion: overviewTrackPointDefaultsVersion,
      trackPointLimit: defaultOverviewTrackPointLimit,
      planePopupDefaultsVersion: overviewPlanePopupDefaultsVersion,
      planePopupFields: [...defaultOverviewPlanePopupFields],
      mapControls: { ...defaultOverviewMapControls },
      mapLayers: { ...defaultOverviewMapLayers },
      mapInteractions: { ...defaultOverviewMapInteractions },
      collapsedPanels: { ...defaultOverviewCollapsedPanels }
    }
  }
  modules.nofly = {
    ...modules.nofly,
    restrictionMap: {
      mapControls: { ...defaultRestrictionMapControls },
      mapInteractions: { ...defaultRestrictionMapInteractions }
    }
  }
  modules.route = {
    ...modules.route,
    designer: {
      mapControls: { ...defaultRouteDesignerMapControls },
      mapInteractions: { ...defaultRouteDesignerMapInteractions }
    }
  }
  modules.register = {
    ...modules.register,
    flightDataMap: {
      mapControls: { ...defaultFlightDataMapControls },
      mapInteractions: { ...defaultFlightDataMapInteractions }
    }
  }
  modules.order = {
    ...modules.order,
    search: {}
  }
  return modules
}

const terminalPreferences = {
  account: {
    // 首次升级时从尚未清理的登录态迁移账号；后续只读取终端偏好。
    lastLoginAccount: savedUserInfo.name || '',
    ...(savedTerminalPreferences.account || {})
  },
  layout: {
    isCollapse: true,
    ...(savedTerminalPreferences.layout || {}),
    language: normalizeLanguage((savedTerminalPreferences.layout && savedTerminalPreferences.layout.language) || 'zh-CN'),
    skin: normalizeTerminalSkin(savedTerminalPreferences.layout && savedTerminalPreferences.layout.skin)
  },
  statusBar: {
    ...Object.keys(defaultStatusBarSettings).reduce((result, key) => {
      result[key] = savedTerminalPreferences.statusBar && savedTerminalPreferences.statusBar[key] !== undefined
        ? savedTerminalPreferences.statusBar[key]
        : defaultStatusBarSettings[key]
      return result
    }, {})
  },
  map: {
    defaultLonLat: null,
    defaultZoom: null,
    styleIndex: 0,
    styleId: '',
    ...(savedTerminalPreferences.map || {})
  },
  plane: planePreferences,
  message: {
    readIds: [],
    ...(savedTerminalPreferences.message || {})
  },
  modules: {
    ...modulePreferences,
    home: {
      ...modulePreferences.home,
      overview: overviewPreferences
    },
    nofly: {
      ...modulePreferences.nofly,
      restrictionMap: restrictionMapPreferences
    },
    route: {
      ...modulePreferences.route,
      designer: routeDesignerPreferences
    },
    register: {
      ...modulePreferences.register,
      flightDataMap: flightDataMapPreferences
    },
    order: {
      ...modulePreferences.order,
      search: savedOrderSearch
    }
  }
}

if (hasLegacyGuidedFov || hasLegacyOverviewPlanePopupDefaults || hasLegacyOverviewTrackPointDefault) {
  setStorageJson('terminalPreferences', terminalPreferences)
}

function saveTerminalPreferences (state) {
  setStorageJson('terminalPreferences', state.terminalPreferences)
}

function getOverviewPreferences (state) {
  const modules = state.terminalPreferences.modules || {}
  const home = modules.home || {}
  return home.overview || {}
}

function setOverviewPreferences (state, partialOverview) {
  const modules = state.terminalPreferences.modules || {}
  const home = modules.home || {}
  state.terminalPreferences.modules = {
    ...modules,
    home: {
      ...home,
      overview: {
        ...(home.overview || {}),
        ...partialOverview
      }
    }
  }
  saveTerminalPreferences(state)
}

function getRestrictionMapPreferences (state) {
  const modules = state.terminalPreferences.modules || {}
  const nofly = modules.nofly || {}
  return nofly.restrictionMap || {}
}

function setRestrictionMapPreferences (state, partialPreferences) {
  const modules = state.terminalPreferences.modules || {}
  const nofly = modules.nofly || {}
  state.terminalPreferences.modules = {
    ...modules,
    nofly: {
      ...nofly,
      restrictionMap: {
        ...(nofly.restrictionMap || {}),
        ...partialPreferences
      }
    }
  }
  saveTerminalPreferences(state)
}

function getRouteDesignerPreferences (state) {
  const modules = state.terminalPreferences.modules || {}
  const route = modules.route || {}
  return route.designer || {}
}

function setRouteDesignerPreferences (state, partialPreferences) {
  const modules = state.terminalPreferences.modules || {}
  const route = modules.route || {}
  state.terminalPreferences.modules = {
    ...modules,
    route: {
      ...route,
      designer: {
        ...(route.designer || {}),
        ...partialPreferences
      }
    }
  }
  saveTerminalPreferences(state)
}

function getFlightDataMapPreferences (state) {
  const modules = state.terminalPreferences.modules || {}
  const register = modules.register || {}
  return register.flightDataMap || {}
}

function setFlightDataMapPreferences (state, partialPreferences) {
  const modules = state.terminalPreferences.modules || {}
  const register = modules.register || {}
  state.terminalPreferences.modules = {
    ...modules,
    register: {
      ...register,
      flightDataMap: {
        ...(register.flightDataMap || {}),
        ...partialPreferences
      }
    }
  }
  saveTerminalPreferences(state)
}

const state = {
  mqttState: false, // mqtt连接状态
  isWebClient: null, // 运行环境 true=浏览器端(BS) false=客户端(CS/Cordova)
  isCordovaAndroid: false,
  isWideScreen: window.innerWidth < 480, // 屏幕宽度是否小于480
  terminalPreferences, // 终端本机长期偏好设置

  /* 页面参数 */
  toMessageIdArr: [], // 用户管理 发布公告页面  id临时传参
  toFlyDataIdArr: [], // 飞机飞行数据 临时传参
  toFlyLogIdArr: [] // 飞机日志 临时传参
}

const mutations = {
  // 保存本终端最后一次成功登录的账号，密码不进入终端偏好。
  setLastLoginAccount (state, username) {
    const normalizedUsername = typeof username === 'string' ? username.trim() : ''
    if (!normalizedUsername) return
    state.terminalPreferences.account = {
      ...(state.terminalPreferences.account || {}),
      lastLoginAccount: normalizedUsername
    }
    saveTerminalPreferences(state)
  },
  setModuleSetting (state, { key, setting }) {
    state.terminalPreferences.modules[key] = {
      ...(state.terminalPreferences.modules[key] || {}),
      ...setting
    }
    saveTerminalPreferences(state)
  },
  resetSettings (state) {
    state.terminalPreferences.modules = createDefaultModulePreferences()
    state.terminalPreferences.statusBar = { ...defaultStatusBarSettings }
    state.terminalPreferences.layout = { isCollapse: true, skin: 'charcoal', language: state.terminalPreferences.layout.language }
    state.terminalPreferences.map = {
      defaultLonLat: null,
      defaultZoom: null,
      styleIndex: 0,
      styleId: ''
    }
    state.terminalPreferences.plane = {}
    saveTerminalPreferences(state)
  },
  // 全局外观与侧栏共用 layout，但切换皮肤只更新自身字段。
  setTerminalSkin (state, skin) {
    if (normalizeTerminalSkin(skin) !== skin) throw new RangeError(`不支持的终端皮肤：${skin}`)
    state.terminalPreferences.layout = { ...state.terminalPreferences.layout, skin }
    saveTerminalPreferences(state)
  },
  setLanguage (state, language) {
    if (normalizeLanguage(language) !== language) throw new RangeError(`Unsupported language: ${language}`)
    state.terminalPreferences.layout = { ...state.terminalPreferences.layout, language }
    saveTerminalPreferences(state)
  },
  // 导航栏 显隐
  setCollapse (state) {
    const isCollapse = !state.terminalPreferences.layout.isCollapse
    state.terminalPreferences.layout.isCollapse = isCollapse
    saveTerminalPreferences(state)
  },
  // 明确设置侧栏折叠状态，供需要进入并恢复临时专注布局的业务使用。
  setCollapseState (state, collapsed) {
    state.terminalPreferences.layout.isCollapse = Boolean(collapsed)
    saveTerminalPreferences(state)
  },
  // 设置 Header 状态按钮显示或隐藏
  setStatusBarButtonVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultStatusBarSettings, key)) return
    state.terminalPreferences.statusBar = {
      ...state.terminalPreferences.statusBar,
      [key]: Boolean(visible)
    }
    saveTerminalPreferences(state)
  },
  // 判断运行环境 true=浏览器端(BS) false=客户端(CS/Cordova)
  setRuntimePlatform (state, runtime = {}) {
    state.isWebClient = runtime.isWebClient !== false
    state.isCordovaAndroid = runtime.isCordovaAndroid === true
  },
  // 设置地图默认经纬度
  setDefaultLonLat (state, lonLat) {
    state.terminalPreferences.map.defaultLonLat = lonLat
    saveTerminalPreferences(state)
  },
  // 设置地图默认缩放值
  setDefaultZoom (state, zoom) {
    state.terminalPreferences.map.defaultZoom = zoom
    saveTerminalPreferences(state)
  },
  // 设置地图样式索引
  setMapStyleIndex (state, index) {
    const mapStyleIndex = Math.max(0, Number(index) || 0)
    state.terminalPreferences.map.styleIndex = mapStyleIndex
    saveTerminalPreferences(state)
  },
  // 地图样式使用稳定 ID 保存，索引仅用于兼容历史终端配置。
  setMapStylePreference (state, { id, index }) {
    state.terminalPreferences.map.styleId = String(id == null ? '' : id)
    state.terminalPreferences.map.styleIndex = Math.max(0, Number(index) || 0)
    saveTerminalPreferences(state)
  },
  // 长期保存概况页视频小窗是否最小化
  setOverviewVideoPipMinimized (state, minimized) {
    setOverviewPreferences(state, {
      videoPipMinimized: Boolean(minimized)
    })
  },
  // 长期保存概况页遥测栏的折叠状态。
  setOverviewTelemetryCollapsed (state, collapsed) {
    setOverviewPreferences(state, {
      telemetryCollapsed: Boolean(collapsed)
    })
  },
  // 长期保存 God 在概况页机队统计中选择的商铺
  setOverviewFleetShopId (state, shopId) {
    setOverviewPreferences(state, {
      fleetShopId: String(shopId || '')
    })
  },
  // 长期保存地面站侧栏收起时打开的操作小窗，三个操作小窗互斥。
  setOverviewCollapsedPanelVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultOverviewCollapsedPanels, key)) return

    const overview = getOverviewPreferences(state)
    const collapsedPanels = {
      ...defaultOverviewCollapsedPanels,
      ...(overview.collapsedPanels || {})
    }

    if (visible) {
      Object.keys(collapsedPanels).forEach(panelKey => {
        collapsedPanels[panelKey] = panelKey === key
      })
    } else {
      collapsedPanels[key] = false
    }

    setOverviewPreferences(state, { collapsedPanels })
  },
  // 设置概况页各地图控件是否显示
  setOverviewMapControlVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultOverviewMapControls, key)) return
    const overview = getOverviewPreferences(state)
    setOverviewPreferences(state, {
      mapControls: {
        ...mergeSupportedSettings(defaultOverviewMapControls, overview.mapControls),
        [key]: Boolean(visible)
      }
    })
  },
  // 长期保存概况页航线、站点等地图内容的显示状态
  setOverviewMapLayerVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultOverviewMapLayers, key)) return
    const overview = getOverviewPreferences(state)
    setOverviewPreferences(state, {
      mapLayers: {
        ...mergeSupportedSettings(defaultOverviewMapLayers, overview.mapLayers),
        [key]: Boolean(visible)
      }
    })
  },
  // 长期保存地面站地图倾斜、旋转等交互手势。
  setOverviewMapInteractionEnabled (state, { key, enabled }) {
    if (!Object.prototype.hasOwnProperty.call(defaultOverviewMapInteractions, key)) return
    const overview = getOverviewPreferences(state)
    setOverviewPreferences(state, {
      mapInteractions: {
        ...defaultOverviewMapInteractions,
        ...(overview.mapInteractions || {}),
        [key]: Boolean(enabled)
      }
    })
  },
  // 设置禁飞区、限飞区编辑页的地图控件显隐。
  setRestrictionMapControlVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultRestrictionMapControls, key)) return
    const preferences = getRestrictionMapPreferences(state)
    setRestrictionMapPreferences(state, {
      mapControls: {
        ...mergeSupportedSettings(defaultRestrictionMapControls, preferences.mapControls),
        [key]: Boolean(visible)
      }
    })
  },
  // 设置禁飞区、限飞区编辑页的旋转、倾斜手势。
  setRestrictionMapInteractionEnabled (state, { key, enabled }) {
    if (!Object.prototype.hasOwnProperty.call(defaultRestrictionMapInteractions, key)) return
    const preferences = getRestrictionMapPreferences(state)
    setRestrictionMapPreferences(state, {
      mapInteractions: {
        ...defaultRestrictionMapInteractions,
        ...(preferences.mapInteractions || {}),
        [key]: Boolean(enabled)
      }
    })
  },
  // 设置航线添加、编辑页地图控件显隐；比例尺为强制控件，不进入此配置。
  setRouteDesignerMapControlVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultRouteDesignerMapControls, key)) return
    const preferences = getRouteDesignerPreferences(state)
    setRouteDesignerPreferences(state, {
      mapControls: {
        ...mergeSupportedSettings(defaultRouteDesignerMapControls, preferences.mapControls),
        [key]: Boolean(visible)
      }
    })
  },
  // 设置航线设计地图的双指倾斜与旋转手势。
  setRouteDesignerMapInteractionEnabled (state, { key, enabled }) {
    if (!Object.prototype.hasOwnProperty.call(defaultRouteDesignerMapInteractions, key)) return
    const preferences = getRouteDesignerPreferences(state)
    setRouteDesignerPreferences(state, {
      mapInteractions: {
        ...defaultRouteDesignerMapInteractions,
        ...(preferences.mapInteractions || {}),
        [key]: Boolean(enabled)
      }
    })
  },
  // 设置飞行数据轨迹地图的控件显隐。
  setFlightDataMapControlVisibility (state, { key, visible }) {
    if (!Object.prototype.hasOwnProperty.call(defaultFlightDataMapControls, key)) return
    const preferences = getFlightDataMapPreferences(state)
    setFlightDataMapPreferences(state, {
      mapControls: {
        ...mergeSupportedSettings(defaultFlightDataMapControls, preferences.mapControls),
        [key]: Boolean(visible)
      }
    })
  },
  // 设置飞行数据轨迹地图的倾斜与旋转手势。
  setFlightDataMapInteractionEnabled (state, { key, enabled }) {
    if (!Object.prototype.hasOwnProperty.call(defaultFlightDataMapInteractions, key)) return
    const preferences = getFlightDataMapPreferences(state)
    setFlightDataMapPreferences(state, {
      mapInteractions: {
        ...defaultFlightDataMapInteractions,
        ...(preferences.mapInteractions || {}),
        [key]: Boolean(enabled)
      }
    })
  },
  // 设置概况页每架飞机保留的实时轨迹点数量
  setOverviewTrackPointLimit (state, limit) {
    const trackPointLimit = Math.min(10000, Math.max(100, Math.round(Number(limit) || defaultOverviewTrackPointLimit)))
    setOverviewPreferences(state, {
      trackPointLimit,
      trackPointDefaultsVersion: overviewTrackPointDefaultsVersion
    })
  },
  // 设置长按飞机状态框显示的属性
  setOverviewPlanePopupFields (state, fields) {
    const selectedFields = Array.isArray(fields)
      ? supportedOverviewPlanePopupFields.filter(key => fields.includes(key))
      : defaultOverviewPlanePopupFields
    setOverviewPreferences(state, {
      planePopupFields: selectedFields
    })
  },
  // 按飞机名称保存最后有效经纬度
  setPlanePosition (state, { planeName, lon, lat, updatedAt }) {
    const key = String(planeName || '').trim()
    const lonValue = Number(lon)
    const latValue = Number(lat)
    const updatedAtValue = Number(updatedAt)
    if (!key || !Number.isFinite(lonValue) || !Number.isFinite(latValue)) return

    const current = state.terminalPreferences.plane[key] || {}
    state.terminalPreferences.plane = {
      ...state.terminalPreferences.plane,
      [key]: {
        ...current,
        lon: lonValue,
        lat: latValue,
        lastPositionAt: Number.isFinite(updatedAtValue) ? updatedAtValue : Date.now()
      }
    }
    saveTerminalPreferences(state)
  },
  // 标记公告已读，避免同一终端重复弹出同一条公告
  markMessageRead (state, messageId) {
    const id = String(messageId || '').trim()
    if (!id) return

    const message = state.terminalPreferences.message || { readIds: [] }
    const readIds = Array.isArray(message.readIds) ? message.readIds.map(item => String(item)) : []

    if (!readIds.includes(id)) {
      readIds.push(id)
    }

    state.terminalPreferences.message = {
      ...message,
      readIds: readIds.slice(-500)
    }
    saveTerminalPreferences(state)
  },
  // 设置订单页面搜索默认值
  setOrderSearch (state, partialSearch) {
    const modules = state.terminalPreferences.modules || {}
    const order = modules.order || {}
    state.terminalPreferences.modules = {
      ...modules,
      order: {
        ...order,
        search: {
          ...(order.search || {}),
          ...partialSearch
        }
      }
    }
    saveTerminalPreferences(state)
  },

  /* 临时传参 */
  // 管理员管理 发布公告页面 传递id数组
  setToMessageIdArr (state, idArr) {
    state.toMessageIdArr = idArr
  },
  // 飞机飞行数据 传递id数组
  setToFlyDataIdArr (state, idArr) {
    state.toFlyDataIdArr = idArr
  },
  // 飞机飞行数据 传递id数组
  setToFlyLogIdArr (state, idArr) {
    state.toFlyLogIdArr = idArr
  }

}

const getters = {
  getTerminalSkin: state => normalizeTerminalSkin(state.terminalPreferences.layout.skin),
  getLanguage: state => normalizeLanguage(state.terminalPreferences.layout.language),
  // 获取侧边导航栏显隐
  getIsCollapse (state) {
    return state.terminalPreferences.layout.isCollapse
  },
  // 获取运行环境 true=浏览器端(BS) false=客户端(CS/Cordova)
  getIsWebClient (state) {
    return state.isWebClient
  },
  getIsCordovaAndroid (state) {
    return state.isCordovaAndroid
  },
  // 获取 Header 状态按钮是否显示
  getStatusBarButtonVisible: (state) => (key) => {
    const statusBar = state.terminalPreferences.statusBar || {}
    return (statusBar[key] === undefined ? defaultStatusBarSettings[key] : statusBar[key]) !== false
  },
  // 获取地图默认经纬度 缓存中没有从 localStorage中获取 也没有设置为0
  getDefaultLonLat (state) {
    return state.terminalPreferences.map.defaultLonLat || { lon: 0, lat: 0 }
  },
  // 获取地图默认缩放值 缓存中没有从 localStorage中获取 也没有设置为1
  getDefaultZoom (state) {
    return state.terminalPreferences.map.defaultZoom || 1
  },
  // 获取地图样式索引
  getMapStyleIndex (state) {
    return Math.max(0, Number(state.terminalPreferences.map.styleIndex) || 0)
  },
  getMapStyleId (state) {
    return String(state.terminalPreferences.map.styleId || '')
  },
  // 获取概况页视频小窗长期保存的最小化状态
  getOverviewVideoPipMinimized (state) {
    return Boolean(getOverviewPreferences(state).videoPipMinimized)
  },
  // 获取概况页遥测栏长期保存的折叠状态。
  getOverviewTelemetryCollapsed (state) {
    return Boolean(getOverviewPreferences(state).telemetryCollapsed)
  },
  // 获取 God 在概况页机队统计中选择的商铺，空值表示所有商铺
  getOverviewFleetShopId (state) {
    return String(getOverviewPreferences(state).fleetShopId || '')
  },
  // 获取地面站侧栏收起时指定操作小窗的长期显示状态。
  getOverviewCollapsedPanelVisible: (state) => (key) => {
    if (!Object.prototype.hasOwnProperty.call(defaultOverviewCollapsedPanels, key)) return false
    const collapsedPanels = getOverviewPreferences(state).collapsedPanels || {}
    return Boolean(collapsedPanels[key])
  },
  // 获取概况页指定地图控件的显示状态
  getOverviewMapControlVisible: (state) => (key) => {
    const overview = getOverviewPreferences(state)
    const mapControls = overview.mapControls || {}
    return Object.prototype.hasOwnProperty.call(defaultOverviewMapControls, key)
      ? mapControls[key] !== false
      : true
  },
  // 获取概况页航线、站点等地图内容的长期显示状态
  getOverviewMapLayerVisible: (state) => (key) => {
    const overview = getOverviewPreferences(state)
    const mapLayers = overview.mapLayers || {}
    return Object.prototype.hasOwnProperty.call(defaultOverviewMapLayers, key)
      ? mapLayers[key] !== false
      : true
  },
  // 获取地面站地图指定交互手势是否启用。
  getOverviewMapInteractionEnabled: (state) => (key) => {
    const overview = getOverviewPreferences(state)
    const mapInteractions = overview.mapInteractions || {}
    return Object.prototype.hasOwnProperty.call(defaultOverviewMapInteractions, key)
      ? Boolean(mapInteractions[key])
      : false
  },
  // 获取禁飞区、限飞区编辑页指定地图控件的显示状态。
  getRestrictionMapControlVisible: (state) => (key) => {
    const preferences = getRestrictionMapPreferences(state)
    const controls = preferences.mapControls || {}
    return Object.prototype.hasOwnProperty.call(defaultRestrictionMapControls, key)
      ? controls[key] !== false
      : false
  },
  // 获取禁飞区、限飞区编辑页指定地图手势的启用状态。
  getRestrictionMapInteractionEnabled: (state) => (key) => {
    const preferences = getRestrictionMapPreferences(state)
    const interactions = preferences.mapInteractions || {}
    return Object.prototype.hasOwnProperty.call(defaultRestrictionMapInteractions, key)
      ? Boolean(interactions[key])
      : false
  },
  // 获取航线添加、编辑页地图控件显示状态。
  getRouteDesignerMapControlVisible: (state) => (key) => {
    const preferences = getRouteDesignerPreferences(state)
    const controls = preferences.mapControls || {}
    return Object.prototype.hasOwnProperty.call(defaultRouteDesignerMapControls, key)
      ? controls[key] !== false
      : false
  },
  // 获取航线设计地图指定手势的启用状态。
  getRouteDesignerMapInteractionEnabled: (state) => (key) => {
    const preferences = getRouteDesignerPreferences(state)
    const interactions = preferences.mapInteractions || {}
    return Object.prototype.hasOwnProperty.call(defaultRouteDesignerMapInteractions, key)
      ? Boolean(interactions[key])
      : false
  },
  // 获取飞行数据轨迹地图指定控件的显示状态。
  getFlightDataMapControlVisible: (state) => (key) => {
    const preferences = getFlightDataMapPreferences(state)
    const controls = preferences.mapControls || {}
    return Object.prototype.hasOwnProperty.call(defaultFlightDataMapControls, key)
      ? controls[key] !== false
      : false
  },
  // 获取飞行数据轨迹地图指定手势的启用状态。
  getFlightDataMapInteractionEnabled: (state) => (key) => {
    const preferences = getFlightDataMapPreferences(state)
    const interactions = preferences.mapInteractions || {}
    return Object.prototype.hasOwnProperty.call(defaultFlightDataMapInteractions, key)
      ? Boolean(interactions[key])
      : false
  },
  // 获取概况页每架飞机保留的实时轨迹点数量
  getOverviewTrackPointLimit (state) {
    const value = Number(getOverviewPreferences(state).trackPointLimit)
    return Number.isFinite(value) ? Math.min(10000, Math.max(100, Math.round(value))) : defaultOverviewTrackPointLimit
  },
  // 获取长按飞机状态框需要显示的属性
  getOverviewPlanePopupFields (state) {
    const fields = getOverviewPreferences(state).planePopupFields
    return Array.isArray(fields)
      ? supportedOverviewPlanePopupFields.filter(key => fields.includes(key))
      : defaultOverviewPlanePopupFields
  },
  // 获取指定飞机的本机偏好
  getPlanePreference: (state) => (planeName) => {
    const key = String(planeName || '').trim()
    return key ? (state.terminalPreferences.plane[key] || {}) : {}
  },
  // 获取模块是否显示
  getModuleVisible: (state) => (key) => {
    const modules = state.terminalPreferences.modules || {}
    return modules[key] ? modules[key].visible !== false : true
  },
  // 判断公告是否已读
  isMessageRead: (state) => (messageId) => {
    const id = String(messageId || '').trim()
    const readIds = state.terminalPreferences.message && Array.isArray(state.terminalPreferences.message.readIds)
      ? state.terminalPreferences.message.readIds.map(item => String(item))
      : []
    return id !== '' && readIds.includes(id)
  },
  // 获取订单页面搜索条件
  getOrderSearch (state) {
    const userInfo = getStorageJson('userInfo', {})
    const modules = state.terminalPreferences.modules || {}
    const order = modules.order || {}
    const defaultValues = {
      shop_id: userInfo.shop_id || null, // 搜索条件 商铺id
      start_time: Math.floor(new Date(new Date().setHours(0, 0, 0, 0)).getTime() / 1000), // 搜索条件 起始时间 默认为今天凌晨0点
      end_time: Math.floor(new Date(new Date().setHours(23, 59, 59, 999)).getTime() / 1000), // 搜索条件 结束时间 默认为今天23点59分59秒
      main_status: [], // 搜索条件 主状态
      shipment_status: [], // 搜索条件 执行状态
      refund_status: [], // 搜索条件 退款状态
      archive_status: [], // 搜索条件 归档状态
      search: ['orderSn', '']// 搜索条件 搜索
    }
    return { ...defaultValues, ...(order.search || {}) }
  },
  // 获取模块列表每页数量
  getModulePageSize: (state) => (key, fallback = 8) => {
    const modules = state.terminalPreferences.modules || {}
    const pageSize = Number(modules[key] && modules[key].pageSize)
    return pageSize > 0 ? Math.min(pageSize, 200) : fallback
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters
}
